import { getCrumbs, getLumber, getStats, getEnergy, getInventory } from '../core/save.js';
import { activeBuffSummary } from '../core/buffs.js';
import { activeQuestList, onQuestEvent, QUESTS } from '../core/quests.js';
import { onCollectionEvent, ITEMS } from '../core/collection.js';
import { formatTime, onTimeChange, getPhase, getHour } from '../core/world.js';
import { getWeatherState, weatherIcon, weatherName } from '../core/weather.js';

// Central HUD controller. Scenes talk through this instead of touching the DOM.
// DOM queries are lazy so this module can be imported before the HTML is parsed.

let _els = null;
function els(){
  if (!_els){
    _els = {
      crumbCount:  document.getElementById('crumbCount'),
      lumber:      document.getElementById('lumber'),
      lumberCount: document.getElementById('lumberCount'),
      energy:      document.getElementById('energy'),
      energyCount: document.getElementById('energyCount'),
      caption:     document.getElementById('caption'),
      titleSub:    document.getElementById('titleSub'),
      enter:       document.getElementById('enterPrompt'),
      hint:        document.getElementById('hint'),
      clock:       document.getElementById('clock'),
      weather:     document.getElementById('weather'),

      // new HUD
      playerSheet:      document.getElementById('playerSheet'),
      sheetBtn:         document.getElementById('playerSheetBtn'),
      sheetPanel:       document.getElementById('playerSheetPanel'),
      sheetClose:       document.querySelector('#playerSheetPanel .sheet-close'),
      sheetCrumb:       document.getElementById('sheetCrumb'),
      sheetCrumbs:      document.getElementById('sheetCrumbs'),
      sheetLumber:      document.getElementById('sheetLumber'),
      sheetEnergy:      document.getElementById('sheetEnergy'),
      sheetStats:       document.getElementById('sheetStats'),
      sheetBuffs:       document.getElementById('sheetBuffs'),
      sheetInv:         document.getElementById('sheetInv'),

      questScrollBtn:   document.getElementById('questScrollBtn'),
      questScrollPanel: document.getElementById('questScrollPanel'),
      questScrollBadge: document.getElementById('questScrollBadge'),

      toastStack:       document.getElementById('toastStack')
    };
  }
  return _els;
}

function fmtKey(id){ return id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); }

export const HUD = {
  refreshClock(){
    const e = els();
    if (e.clock) e.clock.textContent = formatTime();
    if (document.body) document.body.className = document.body.className.replace(/time-\w+/g, '').trim() + ' time-' + getPhase(getHour());
  },

  refreshWeather(){
    const e = els().weather;
    if (!e) return;
    const st = getWeatherState();
    e.textContent = `${weatherIcon(st.weather)} ${weatherName(st.weather)} · ${st.temp}°C · ${st.season}`;
  },

  refreshCrumbs(){
    const n = getCrumbs();
    if (els().crumbCount) els().crumbCount.textContent = n;
    if (els().sheetCrumb) els().sheetCrumb.textContent = n;
    if (els().sheetCrumbs) els().sheetCrumbs.textContent = n;
  },

  refreshEnergy(){
    const n = getEnergy();
    const e = els().energy;
    if (e){
      e.classList.toggle('low', n < 20);
      els().energyCount.textContent = n;
    }
    if (els().sheetEnergy) els().sheetEnergy.textContent = n;
  },

  refreshLumber(){
    const n = getLumber();
    if (els().lumberCount) els().lumberCount.textContent = n;
    if (els().lumber) els().lumber.hidden = n <= 0;
    if (els().sheetLumber) els().sheetLumber.textContent = n;
  },

  refreshStats(){
    const s = getStats();
    const e = els().stats;
    const txt = `STR ${Math.floor(s.str)} · AGI ${Math.floor(s.agi)} · INT ${Math.floor(s.int)} · ZEN ${Math.floor(s.zen)}`;
    if (e) e.textContent = txt;
    if (els().sheetStats) els().sheetStats.textContent = txt;

    const list = activeBuffSummary();
    const html = list.length
      ? list.map(x => `<span>+${x.amount} ${x.stat.toUpperCase()} ${Math.floor(x.remaining / 60)}:${String(x.remaining % 60).padStart(2, '0')}</span>`).join('')
      : '';
    if (els().buffs){
      els().buffs.innerHTML = html;
      els().buffs.hidden = list.length === 0;
    }
    if (els().sheetBuffs) els().sheetBuffs.innerHTML = html;
  },

  _renderInventory(){
    const inv = getInventory();
    const out = [];
    const section = (title, obj) => {
      const keys = Object.keys(obj || {}).filter(k => (obj[k] || 0) > 0);
      if (!keys.length) return '';
      const rows = keys.map(k => `<div>${obj[k]} ${fmtKey(k)}</div>`).join('');
      return `<div class="inv-section"><div class="inv-title">${title}</div><div class="inv-list">${rows}</div></div>`;
    };
    out.push(section('Seeds', inv.seeds));
    out.push(section('Crops', inv.crops));
    out.push(section('Ingredients', inv.ingredients));
    out.push(section('Dishes', inv.dishes));
    if (els().sheetInv) els().sheetInv.innerHTML = out.join('');
  },

  say(text){ els().caption.textContent = text; },

  setSubtitle(text){ els().titleSub.textContent = text; },

  showEnter(label, onClick){
    const e = els().enter;
    if (!label){ e.hidden = true; e.onclick = null; return; }
    e.hidden = false;
    e.textContent = label;
    e.onclick = onClick;
  },

  hideHintSoon(ms = 8000){ setTimeout(() => els().hint.classList.add('gone'), ms); },
  hideHint(){ els().hint.classList.add('gone'); },
  setHint(text){ els().hint.textContent = text; els().hint.classList.remove('gone'); },

  // ---- bread-shaped player sheet ----
  togglePlayerSheet(force){
    const e = els();
    if (!e.sheetPanel) return;
    const show = force ?? e.sheetPanel.hidden;
    e.sheetPanel.hidden = !show;
    if (show){
      this.refreshCrumbs(); this.refreshLumber(); this.refreshEnergy();
      this.refreshStats(); this._renderInventory();
    }
  },

  // ---- quest scroll ----
  toggleQuestScroll(force){
    const e = els();
    if (!e.questScrollPanel) return;
    const show = force ?? e.questScrollPanel.hidden;
    e.questScrollPanel.hidden = !show;
    if (show) this.refreshQuests();
  },

  refreshQuests(){
    const e = els();
    const list = activeQuestList();
    if (e.questScrollBtn) e.questScrollBtn.hidden = list.length === 0;
    if (e.questScrollBadge) e.questScrollBadge.textContent = list.length;
    if (!e.questScrollPanel) return;
    if (list.length === 0){ e.questScrollPanel.innerHTML = ''; return; }
    e.questScrollPanel.innerHTML = list.map(q => {
      const obj = q?.objective || {};
      const pct = q?.goal ? Math.round(((q?.have || 0) / q.goal) * 100) : 0;
      const meter = obj.kind === 'flag'
        ? `<div class="q-obj ${q?.ready ? 'done' : ''}">${q?.ready ? '✓ ' : ''}${obj.noun || ''}</div>`
        : `<div class="q-meter"><span style="width:${pct}%"></span></div>
           <div class="q-obj">${q?.have || 0} / ${q?.goal || 0} ${obj.noun || ''}${q?.ready ? ' ✓' : ''}</div>`;
      return `<div class="q-item ${q?.ready ? 'ready' : ''}">
        <b>${q?.title || 'Quest'}</b>
        <p>${q?.desc || ''}</p>
        ${meter}
        ${q?.ready ? '<div class="q-turnin">↩ return to the giver to turn in</div>' : ''}
      </div>`;
    }).join('');
  },

  // ---- toast / achievement notifications ----
  toast({ icon = '✨', title, sub = '', duration = 3200 }){
    const stack = els().toastStack;
    if (!stack) return;
    const card = document.createElement('div');
    card.className = 'toast-card';
    card.innerHTML = `<div class="toast-icon">${icon}</div><div class="toast-text"><div class="toast-title">${title}</div>${sub ? `<div class="toast-sub">${sub}</div>` : ''}</div>`;
    stack.appendChild(card);
    requestAnimationFrame(() => card.classList.add('show'));
    setTimeout(() => {
      card.classList.remove('show');
      setTimeout(() => card.remove(), 350);
    }, duration);
    while (stack.children.length > 4) stack.firstElementChild.remove();
  },

  // Legacy single-toast alias: route through the new stack.
  questToast(text){
    this.toast({ icon: '📜', title: text });
  }
};

// Wire the new buttons once at load.
function bindHud(){
  const e = els();
  if (e.sheetBtn) e.sheetBtn.onclick = () => HUD.togglePlayerSheet();
  if (e.sheetClose) e.sheetClose.onclick = () => HUD.togglePlayerSheet(false);
  if (e.questScrollBtn) e.questScrollBtn.onclick = () => HUD.toggleQuestScroll();

  // click outside closes open panels
  document.addEventListener('pointerdown', ev => {
    if (!e.playerSheet || !e.questScrollBtn) return;
    const inSheet = e.playerSheet.contains(ev.target);
    const inQuest = e.questScrollBtn.contains(ev.target) || e.questScrollPanel.contains(ev.target);
    if (!inSheet && !e.sheetPanel.hidden) HUD.togglePlayerSheet(false);
    if (!inQuest && !e.questScrollPanel.hidden) HUD.toggleQuestScroll(false);
  });

  // escape closes panels
  document.addEventListener('keydown', ev => {
    if (ev.key !== 'Escape') return;
    if (e.sheetPanel && !e.sheetPanel.hidden) HUD.togglePlayerSheet(false);
    if (e.questScrollPanel && !e.questScrollPanel.hidden) HUD.toggleQuestScroll(false);
  });

  HUD.refreshClock();
  HUD.refreshStats();
  HUD.refreshEnergy();
  HUD.refreshWeather();
  HUD.refreshCrumbs();
  HUD.refreshLumber();
  HUD.refreshQuests();
}

if (document.readyState === 'loading'){
  document.addEventListener('DOMContentLoaded', bindHud);
} else {
  bindHud();
}

onTimeChange(() => { HUD.refreshClock(); HUD.refreshWeather(); });

onQuestEvent(({ type, quest }) => {
  const title = QUESTS[quest]?.title || 'Quest';
  const reward = QUESTS[quest]?.reward;
  const sub = reward?.crumbs ? `+${reward.crumbs} crumbs` : '';
  const map = {
    accepted:  { icon: '📜', title: `Quest started — ${title}` },
    progress:  { icon: '📜', title: `${title}`, sub: 'progress updated' },
    ready:     { icon: '✨', title: `${title}`, sub: 'objective complete! Return to turn in.' },
    completed: { icon: '✅', title: `Quest complete — ${title}`, sub }
  };
  const t = map[type];
  if (t) HUD.toast({ ...t, duration: type === 'completed' ? 4200 : 3200 });
  HUD.refreshQuests();
});

onCollectionEvent(({ id, first }) => {
  if (!first) return;
  const item = ITEMS[id];
  HUD.toast({
    icon: item?.icon || '🔎',
    title: item ? `${item.name} added to collection` : 'New find!',
    sub: item ? `${item.rarity} ${item.kind}` : ''
  });
});
